"use client";

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2, LogIn } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { login } from '@/lib/services/authService';
import { ApiError } from '@/lib/errors/apiErrors';

const loginSchema = z.object({
    email: z.string().email("L'adresse email est invalide."),
    password: z.string().min(1, "Le mot de passe est requis."),
});

type LoginFormValues = z.infer<typeof loginSchema>;

export default function LoginPage() {
    const router = useRouter();
    const [error, setError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const form = useForm<LoginFormValues>({
        resolver: zodResolver(loginSchema),
        defaultValues: { email: '', password: '' },
    });

    const onSubmit = async (values: LoginFormValues) => {
        setError(null);
        setIsSubmitting(true);
        try {
            await login(values);
            router.push('/dashboard'); // Redirection vers le tableau de bord après succès
        } catch (err) {
            if (err instanceof ApiError) {
                setError(err.message);
            } else {
                setError("Une erreur inattendue est survenue.");
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div 
            className="flex min-h-screen items-center justify-center relative bg-cover bg-center"
            style={{ backgroundImage: 'url(/bg.png)' }}
        >
            <Card className="w-full max-w-md z-10 shadow-2xl bg-white/95 dark:bg-gray-950/95 backdrop-blur border-0 rounded-2xl p-2 pb-0">
                <CardHeader className="text-center pb-4">
                    <div className="flex justify-center mb-6 mt-4">
                        <img src="/logo.png" alt="Horizon Logo" className="h-10 w-auto object-contain" />
                    </div>
                    <CardTitle className="text-2xl font-bold tracking-tight">Se connecter</CardTitle>
                </CardHeader>
                <CardContent className="px-8 pb-8">
                    <Form {...form}>
                        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
                            <FormField
                                control={form.control}
                                name="email"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel className="text-xs font-semibold text-gray-500 uppercase tracking-widest">Email</FormLabel>
                                        <FormControl>
                                            <Input type="email" placeholder="horizon@somayar.com" className="h-11 bg-gray-50/50" {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="password"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel className="text-xs font-semibold text-gray-500 uppercase tracking-widest">Password</FormLabel>
                                        <FormControl>
                                            <Input type="password" placeholder="••••••••" className="h-11 bg-gray-50/50" {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            {error && (
                                <p className="text-sm text-destructive font-medium">{error}</p>
                            )}
                            <div className="pt-2">
                                <Button type="submit" className="w-full h-11 bg-gradient-to-r from-amber-600 to-blue-900 hover:from-amber-700 hover:to-blue-950 text-white font-medium shadow-md transition-all" disabled={isSubmitting}>
                                    {isSubmitting ? (
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    ) : null}
                                    Se connecter
                                </Button>
                            </div>
                    
                        </form>
                    </Form>
                </CardContent>
            </Card>
        </div>
    );
}
